<script setup lang="ts">
  import { computed, onMounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import FlowCard from '@/shared/components/rebranding/FlowCard/FlowCard.vue'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import ListCard from './components/ListCard.vue'
  import { setFormatDate } from '@/shared/utils'
  import { PolicyListItem } from '@/shared/types/policy.types'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'

  interface Props {
    passport?: string
    birthdate?: Date | string
    policyList: PolicyListItem[]
    homeUrl?: string
  }
  interface Emits {
    (e: 'back'): void
  }

  const props = withDefaults(defineProps<Props>(), {
    passport: '',
    birthdate: '',
    homeUrl: '',
  })

  const emit = defineEmits<Emits>()

  const gaService = useGoogleAnalytics()
  const { locale } = useI18n()

  const subtitle = computed(() => {
    if (props.passport && props.birthdate) {
      return `${props.passport}\u00A0\u00B7\u00A0${setFormatDate(new Date(props.birthdate), locale.value as LocaleType)}`
    }
    return ''
  })

  const sendAvailablePoliciesEnter = () => {
    const policyIds = props.policyList.map((policy) => policy.policyNo)
    gaService.sendPolicyEvents('view_available_policies', 'available_policies', policyIds)
  }

  onMounted(() => {
    sendAvailablePoliciesEnter()
  })
</script>

<template>
  <FlowCard
    :title="$t('qic_policies_list.policies_list.header')"
    :subtitle="subtitle"
    class="policy-download-list-qc"
    with-back-button
    :title-wrap="false"
    :with-button="false"
    @back="emit('back')"
  >
    <template #subtitle>
      <p class="text-regular policy-download-list-qc__subtitle">{{ subtitle }}</p>
    </template>
    <WrapperQC direction="column" gap="var(--spacing-6)">
      <ListCard v-for="policy in policyList" :key="policy.policyNo" :item="policy" />

      <ButtonQC variant="outline" :href="homeUrl" wide data-test="go-home-link">
        {{ $t('qic_policies_list.policies_list.go_to_main_page.button') }}
      </ButtonQC>
    </WrapperQC>
  </FlowCard>
</template>

<style lang="scss" scoped>
  @use '@scss/utils.scss';

  .policy-download-list-qc {
    padding: var(--spacing-8);

    @include utils.mobile-flow {
      padding: var(--spacing-8) var(--spacing-5);
    }
  }

  .policy-download-list-qc__subtitle {
    word-wrap: break-word;
  }
</style>
